import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, Calendar, RefreshCw, User, ExternalLink, ArrowUpRight, ArrowRight, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import SEO from "../components/SEO";
import JsonLd from "../components/JsonLd";
import { SITE_URL } from "../components/OrganizationSchema";
import Markdown from "../components/Markdown";
import FinalCta from "../sections/FinalCta";
import { AUTHOR } from "../data/authorData";
import Sources from "../components/Sources";
import { matchServicesFor, matchProjectsFor, matchPostsFor } from "../lib/related";
import { useLang } from "../i18n/LanguageContext";
import { BLOG_DETAIL_I18N } from "../i18n/content";
import { SERVICES_EN } from "../i18n/services";

const formatDate = (iso, locale) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

export default function BlogDetail() {
  const { slug } = useParams();
  const { lang } = useLang();
  const t = BLOG_DETAIL_I18N[lang];
  const [post, setPost] = useState(null);
  const [err, setErr] = useState("");
  const [projects, setProjects] = useState([]);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    setPost(null);
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setErr("notfound"));
  }, [slug]);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => {});
    api.get("/blog").then((r) => setPosts(r.data || [])).catch(() => {});
  }, []);

  if (err) {
    return (
      <div className="container-x py-32 text-center">
        <h1 className="h2-section">{t.notFound}</h1>
        <Link to="/blog" className="mt-6 inline-flex btn-secondary">{t.backToBlog}</Link>
      </div>
    );
  }
  if (!post) return <div className="container-x py-32 text-center text-[#334155]">{t.loading}</div>;

  const postUrl = `${SITE_URL}/blog/${post.slug}`;
  const faq = (post.faq || []).filter((x) => x?.q && x?.a);
  const matchText = `${post.title} ${post.category || ""} ${post.excerpt || ""}`;
  const relatedServices = matchServicesFor(matchText, post.tags, 3);
  const relatedProjects = matchProjectsFor(projects, matchText, post.tags, 3);
  const relatedPosts = matchPostsFor(posts, post.slug, post.category, 3);
  const published = formatDate(post.created_at, t.locale);
  const updated = formatDate(post.updated_at, t.locale);
  const showUpdated = updated && updated !== published;

  const graph = [
    {
      "@type": "BlogPosting",
      "@id": `${postUrl}#article`,
      headline: post.title,
      description: post.seo_description || post.excerpt || "",
      ...(post.cover_image ? { image: [post.cover_image] } : {}),
      ...(post.category ? { articleSection: post.category } : {}),
      ...(post.tags?.length ? { keywords: post.tags.join(", ") } : {}),
      datePublished: post.created_at,
      dateModified: post.updated_at || post.created_at,
      author: {
        "@type": "Person",
        name: AUTHOR.name,
        jobTitle: AUTHOR.jobTitle,
        url: AUTHOR.url,
        ...(AUTHOR.image ? { image: `${SITE_URL}${AUTHOR.image}` } : {}),
        ...(AUTHOR.sameAs?.length ? { sameAs: AUTHOR.sameAs } : {}),
        worksFor: { "@id": `${SITE_URL}/#organization` },
      },
      publisher: { "@id": `${SITE_URL}/#organization` },
      mainEntityOfPage: postUrl,
      inLanguage: t.inLanguage,
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: t.breadcrumbHome, item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
        { "@type": "ListItem", position: 3, name: post.title, item: postUrl },
      ],
    },
  ];
  if (faq.length) {
    graph.push({
      "@type": "FAQPage",
      "@id": `${postUrl}#faq`,
      mainEntity: faq.map((x) => ({
        "@type": "Question",
        name: x.q,
        acceptedAnswer: { "@type": "Answer", text: x.a },
      })),
    });
  }
  const articleJsonLd = { "@context": "https://schema.org", "@graph": graph };

  return (
    <>
      <SEO title={post.seo_title || `${post.title} | Dijital Roket Blog`} description={post.seo_description || post.excerpt} image={post.cover_image} />
      <JsonLd id="blog-post" data={articleJsonLd} />
      <section className="relative overflow-hidden bg-[#07111F] text-white">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="absolute -top-20 left-1/3 h-[420px] w-[420px] rounded-full bg-[#2563EB]/25 blur-[120px]" />
        <div className="container-x relative pt-24 pb-16 max-w-3xl">
          <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> {t.allPosts}
          </Link>
          <nav aria-label="breadcrumb" className="mt-4">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-white/50" data-testid="blog-breadcrumb">
              <li><Link to="/" className="hover:text-white">{t.breadcrumbHome}</Link></li>
              <li aria-hidden="true">/</li>
              <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
              <li aria-hidden="true">/</li>
              <li className="text-white/80" aria-current="page">{post.title}</li>
            </ol>
          </nav>
          <div className="mt-6 block"><span className="eyebrow-light">{post.category}</span></div>
          <h1 className="mt-3 h1-display text-white">{post.title}</h1>
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/60" data-testid="blog-meta">
            <span className="inline-flex items-center gap-1.5"><User className="h-3.5 w-3.5 text-[#22D3EE]" /> {AUTHOR.name}</span>
            {published && <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-[#22D3EE]" /> {published}</span>}
            {showUpdated && <span className="inline-flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5 text-[#22D3EE]" /> {t.updated} {updated}</span>}
            <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-[#22D3EE]" /> {t.readTime(post.read_time)}</span>
          </div>
        </div>
      </section>

      {post.cover_image && (
        <div className="container-x -mt-10 max-w-3xl">
          <img src={post.cover_image} alt={post.title} className="rounded-2xl border border-slate-200 shadow-xl w-full h-[280px] sm:h-[380px] object-cover" />
        </div>
      )}

      <article className="section bg-white">
        <div className="container-x max-w-3xl">
          <Markdown source={post.content} />
        </div>
      </article>

      {/* First-Party Knowledge — Dijital Roket Deneyimi (yalnızca gerçek bilgi girildiyse) */}
      {(post.expert_insight || post.project_example || post.methodology) && (
        <section className="pb-12 bg-white" data-testid="blog-first-party">
          <div className="container-x max-w-3xl">
            <div className="rounded-2xl border border-[#2563EB]/20 bg-gradient-to-br from-[#2563EB]/5 to-[#22D3EE]/5 p-6 sm:p-8">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-[#2563EB] to-[#22D3EE] text-white"><Sparkles className="h-4 w-4" /></span>
                <h2 className="font-heading text-xl font-bold text-[#07111F]">{t.firstPartyTitle}</h2>
              </div>
              <p className="mt-1 text-xs text-slate-500">{t.firstPartyNote}</p>
              <div className="mt-5 space-y-5">
                {post.expert_insight && (
                  <div data-testid="blog-expert-insight">
                    <h3 className="font-heading text-sm font-bold uppercase tracking-[0.14em] text-[#2563EB]">{t.expertInsight}</h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-[#334155]">{post.expert_insight}</p>
                  </div>
                )}
                {post.project_example && (
                  <div data-testid="blog-project-example">
                    <h3 className="font-heading text-sm font-bold uppercase tracking-[0.14em] text-[#2563EB]">{t.projectExample}</h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-[#334155]">{post.project_example}</p>
                  </div>
                )}
                {post.methodology && (
                  <div data-testid="blog-methodology">
                    <h3 className="font-heading text-sm font-bold uppercase tracking-[0.14em] text-[#2563EB]">{t.methodology}</h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-[#334155]">{post.methodology}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {faq.length > 0 && (
        <section className="pb-16 bg-white" data-testid="blog-faq">
          <div className="container-x max-w-3xl">
            <span className="eyebrow">{t.faqEyebrow}</span>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-bold text-[#07111F]">
              {t.faqTitle}
            </h2>
            <dl className="mt-8 space-y-4">
              {faq.map((x, i) => (
                <div key={i} className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-6" data-testid={`blog-faq-item-${i}`}>
                  <dt className="font-heading text-base font-bold text-[#07111F]">{x.q}</dt>
                  <dd className="mt-2 text-[15px] leading-relaxed text-[#334155]">{x.a}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      )}

      {/* Author E-E-A-T */}
      <section className="pb-12 bg-white" data-testid="blog-author">
        <div className="container-x max-w-3xl">
          <div className="flex flex-col sm:flex-row gap-5 rounded-2xl border border-slate-200 bg-[#F8FAFC] p-6">
            {AUTHOR.image ? (
              <img src={AUTHOR.image} alt={AUTHOR.name} className="h-16 w-16 shrink-0 rounded-full object-cover border border-slate-200" data-testid="blog-author-photo" />
            ) : (
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#2563EB] to-[#22D3EE] text-white font-heading text-xl font-bold" aria-hidden="true">
                {AUTHOR.initials}
              </div>
            )}
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.16em] text-[#2563EB]">{t.authorLabel}</div>
              <h3 className="mt-1 font-heading text-lg font-bold text-[#07111F]">{AUTHOR.name}</h3>
              <div className="text-sm font-medium text-[#334155]">{AUTHOR.title}</div>
              <p className="mt-3 text-sm leading-relaxed text-[#334155]">{AUTHOR.bio}</p>
              <a href={AUTHOR.url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2563EB] hover:gap-2 transition-all" data-testid="blog-author-link">
                {AUTHOR.url.replace(/^https?:\/\//, "").replace(/\/$/, "")} <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Internal links: related services */}
      {relatedServices.length > 0 && (
        <section className="pb-4 bg-white">
          <div className="container-x max-w-3xl">
            <h2 className="font-heading text-xl font-bold text-[#07111F]">{t.relatedServices}</h2>
            <div className="mt-4 flex flex-wrap gap-3">
              {relatedServices.map((s) => (
                <Link key={s.slug} to={`/hizmetler/${s.slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-[#F8FAFC] px-4 py-2 text-sm font-medium text-[#334155] hover:border-[#2563EB]/40 hover:text-[#07111F] transition" data-testid={`blog-rel-service-${s.slug}`}>
                  {t.serviceChip(lang === "en" ? SERVICES_EN[s.slug]?.navLabel || s.navLabel : s.navLabel)} <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Internal links: related projects */}
      {relatedProjects.length > 0 && (
        <section className="section bg-white">
          <div className="container-x">
            <span className="eyebrow">{t.relatedProjectsEyebrow}</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-[#07111F]">{t.relatedProjectsTitle}</h2>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedProjects.map((p) => (
                <Link key={p.slug} to={`/projeler/${p.slug}`} className="group rounded-2xl overflow-hidden border border-slate-200 bg-white transition hover:-translate-y-1 hover:shadow-xl" data-testid={`blog-rel-project-${p.slug}`}>
                  <div className="h-36 overflow-hidden bg-[#0B1728]">
                    {p.cover_image && <img src={p.cover_image} alt={p.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                  </div>
                  <div className="p-5">
                    <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#2563EB]">{p.sector}</span>
                    <h3 className="mt-1.5 font-heading text-base font-semibold text-[#07111F] leading-snug line-clamp-2">{p.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Internal links: related posts */}
      {relatedPosts.length > 0 && (
        <section className="pb-16 bg-white">
          <div className="container-x">
            <h2 className="font-heading text-xl font-bold text-[#07111F]">{t.relatedPosts}</h2>
            <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedPosts.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="group card-elevate p-5" data-testid={`blog-rel-post-${p.slug}`}>
                  <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#2563EB]">{p.category}</span>
                  <h3 className="mt-1.5 font-heading text-base font-semibold text-[#07111F] leading-snug line-clamp-2">{p.title}</h3>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#2563EB] group-hover:gap-1.5 transition-all">{t.read} <ArrowRight className="h-3.5 w-3.5" /></span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Sources sources={post.sources} title={t.sourcesTitle} />

      <FinalCta />
    </>
  );
}
